import styled from '@emotion/styled';
import { useResource } from '@landsbankinn/l16n';
import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Close } from '../../components/icons';
import { device, sronly, typography } from '../../styles';
import { theme } from '../../styles/theme';
import res from './Dialog.res';

interface IProps {
  children: React.ReactNode;
  title: { label: string | JSX.Element; hidden?: boolean };
  onClose?: () => void;
  removeContainarTopPadding?: boolean;
}

const Host = styled.div`
  background-color: ${theme.colors.background};
  position: fixed;
  overflow: scroll;
  top: 0;
  left: 0;
  min-width: 100vw;
  min-height: 100vh;
  z-index: 101;

  @media ${device.desktop} {
    background: rgba(21, 46, 72, 0.9);
  }
`;

const AbsoluteContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 101;
  background: white;
  @media ${device.desktop} {
    margin: 90px 130px 90px 130px;
    width: calc(100% - 260px);
  }
`;

const Container = styled.div`
  margin-left: 44px;
  margin-right: 44px;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  min-height: 100vh;
  flex: 1;
  padding-bottom: 144px;

  @media ${device.desktop} {
    margin-left: 100px;
    margin-right: 100px;
  }
`;

const Header = styled.div`
  position: fixed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background-color: ${theme.shades.primary2};
  z-index: 101;
  padding: 23px 44px 25px 44px;
`;

const Title = styled.h2<{ hidden?: boolean }>`
  ${typography.body}
  cursor: pointer;
  font-weight: bold;
  ${(props) => (props.hidden ? sronly : '')}
`;

const HeaderBackButton = styled.button`
  cursor: pointer;
  width: 30px;
`;

const ChildContainer = styled.div<{
  noHeader?: boolean;
  removeConatinerTopPadding?: boolean;
}>`
  padding-top: ${(props) => {
    if (!props.noHeader) {
      return props.removeConatinerTopPadding ? '72px;' : '156px;';
    }
    return '20px;';
  }};
`;

export function Dialog({
  children,
  onClose,
  title,
  removeContainarTopPadding,
}: IProps) {
  const { r } = useResource();
  const titleRef = useRef<HTMLHeadingElement>(null);

  const root = useMemo(() => {
    if (typeof window !== 'undefined') {
      return document.getElementById('portal');
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.body.style.overflow = 'hidden';
      root?.setAttribute('inert', 'true');

      return () => {
        document.documentElement.style.overflow = '';
        root?.setAttribute('inert', 'false');
      };
    }
  }, [root]);

  useEffect(() => {
    if (titleRef && titleRef.current) {
      titleRef.current.focus();
    }
  }, [titleRef]);

  if (!root) {
    return null;
  }

  return createPortal(
    <Host role="dialog" aria-modal data-cy="overlayDialog">
      <AbsoluteContainer>
        {onClose !== undefined ? (
          <Header>
            <Title tabIndex={-1} ref={titleRef} hidden={title.hidden}>
              {title.label}
            </Title>
            <HeaderBackButton onClick={onClose}>
              <Close aria-label={r(res.closeAriaLabel)} />
            </HeaderBackButton>
          </Header>
        ) : (
          <Title tabIndex={-1} ref={titleRef} hidden>
            {title.label}
          </Title>
        )}

        <Container>
          <ChildContainer
            removeConatinerTopPadding={removeContainarTopPadding}
            noHeader={onClose === undefined}
          >
            {children}
          </ChildContainer>
        </Container>
      </AbsoluteContainer>
    </Host>,
    root,
  );
}
